/**
 * Hook reutilizável para paginação client-side de arrays.
 * Usado quando o backend devolve a lista completa e queremos paginar localmente.
 *
 * Uso:
 *   const { paginated, pagination, setPage } = useClientPagination(items, 10);
 *   {paginated.map(...)}
 *   <GridPagination pagination={pagination} onPageChange={setPage} />
 */
import { useState, useMemo, useEffect } from 'react';

export default function useClientPagination(items, pageSize = 10) {
  const [page, setPage] = useState(1);
  const safeItems = Array.isArray(items) ? items : [];
  const total = safeItems.length;
  const total_pages = Math.max(1, Math.ceil(total / pageSize));

  // Resetar para página 1 quando a lista muda de tamanho ou ficamos fora do range
  useEffect(() => {
    if (page > total_pages) setPage(1);
  }, [total_pages, page]);

  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return safeItems.slice(start, start + pageSize);
  }, [safeItems, page, pageSize]);

  const pagination = useMemo(() => ({
    page,
    limit: pageSize,
    total,
    total_pages,
    has_next: page < total_pages,
    has_prev: page > 1,
  }), [page, pageSize, total, total_pages]);

  return { paginated, pagination, setPage, page };
}
